import { computed, ref, Ref, watch } from "vue"
import { Highlight } from "suus-api"
import { GalleryMedium } from "features/gallery/gallery.types"

type AddListener = (highlight: Highlight) => void
type RemoveListener = (galleryId: number) => void

export class GalleryService {
  constructor(
    private _canEdit: Ref<boolean>,
    private _addGalleryToHighlight: (galleryId: number) => Promise<Highlight>,
    private _removeGalleryFromHighlight: (
      gallery: GalleryMedium
    ) => Promise<void>,
    private _reorderHighlights: ({
      id,
      index,
    }: {
      id: number
      index: number
    }) => Promise<void>,
    private _initialHighlightsCount: Ref<number>,
    public initialShowUploader: Ref<boolean>
  ) {
    watch(
      () => _initialHighlightsCount.value,
      () => {
        this._optimisticHighlightsCount.value = 0
      },
      { immediate: true }
    )
  }

  canEdit = computed(() => this._canEdit.value)

  addGalleryToHighlight = async (galleryId: number) => {
    console.log("addGalleryToHighlight", galleryId)
    if (this._addGalleryToHighlight && this.canEdit.value) {
      const highlight = await this._addGalleryToHighlight(galleryId)
      this.addListeners.forEach((l) => l(highlight))
      this._optimisticHighlightsCount.value++
      return highlight
    }
    return null
  }

  removeGalleryFromHighlight = async (gallery: GalleryMedium) => {
    console.log("removeGalleryFromHighlight", gallery)
    if (this._removeGalleryFromHighlight && this.canEdit.value) {
      await this._removeGalleryFromHighlight(gallery)
      this.removeListeners.forEach((l) => l(gallery.id))
      this._optimisticHighlightsCount.value--
    }
  }

  reorderHighlights = async ({ id, index }: { id: number; index: number }) => {
    console.log("reorderHighlights")
    if (this._reorderHighlights && this.canEdit.value) {
      await this._reorderHighlights({ id, index })
      this.reorderListeners.forEach((l) => l())
    }
  }

  private addListeners: AddListener[] = []
  private removeListeners: RemoveListener[] = []
  private reorderListeners: (() => void)[] = []

  registerAddListener = (listener: AddListener) => {
    this.addListeners.push(listener)
  }

  removeAddListener = (listener: AddListener) => {
    this.addListeners = this.addListeners.filter((l) => l !== listener)
  }

  registerRemoveListener = (listener: RemoveListener) => {
    this.removeListeners.push(listener)
  }

  removeRemoveListener = (listener: RemoveListener) => {
    this.removeListeners = this.removeListeners.filter((l) => l !== listener)
  }

  registerReorderListener = (listener: () => void) => {
    this.reorderListeners.push(listener)
  }

  removeReorderListener = (listener: () => void) => {
    this.reorderListeners = this.reorderListeners.filter((l) => l !== listener)
  }

  private _optimisticHighlightsCount = ref(0)
  public highlightsCount = computed(() => {
    return Math.max(
      this._initialHighlightsCount.value +
        this._optimisticHighlightsCount.value,
      0
    )
  })
}
